/**
 * Time Machine #114 Phase 4.5 — pure response parser for the Lambda's new
 * `getStateAndEventsForDate` method. Sister to `parseTimeTravelResponse` for
 * the legacy `getStateAtTimestamp`.
 *
 * Successful response shape:
 *   {
 *     baselineState: [...full reconstructed docs at 00:00 UTC of date],
 *     events: [...EventLog entries for the date],
 *     baselineSnapshot: { batchId, timestamp, version },
 *     seedToStartReplayWindow: { from, to, durationMs, eventsApplied, applied },
 *     dayWindow: { from, to },
 *     meta: { targetCollection, location, date, ..., handlerDurationMs }
 *   }
 *
 * Floor refusal: { error, floor, requested, targetCollection }
 *
 * Lambda runtime / API Gateway error shapes are detected upstream by the
 * shared envelope check (Lambda errorType/errorMessage, API Gateway message
 * string). We don't duplicate those — the caller can use the same upstream
 * detection. This parser focuses on the structured-success vs floor-refusal
 * branch once the envelope is known to be `{message: {method, result}}`.
 */

export function parseStateAndEventsResponse(rawResponse) {
  if (rawResponse == null || typeof rawResponse !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response is not an object",
      raw: rawResponse,
    };
  }

  const { message, _debugVersion, errorType, errorMessage } = rawResponse;

  // Lambda runtime error: {errorType, errorMessage}
  if (errorType || errorMessage) {
    return {
      ok: false,
      kind: "error",
      reason: errorMessage || errorType || "Lambda error (no message)",
      lambdaErrorType: errorType || null,
      raw: rawResponse,
    };
  }

  // API Gateway error string: {message: "<plain string>"}
  if (typeof message === "string") {
    return {
      ok: false,
      kind: "error",
      reason: `API Gateway: ${message}`,
      raw: rawResponse,
    };
  }

  if (!message || typeof message !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response missing message envelope",
      raw: rawResponse,
    };
  }

  if (message.method !== "getStateAndEventsForDate") {
    return {
      ok: false,
      kind: "error",
      reason: `Expected message.method "getStateAndEventsForDate" (got ${JSON.stringify(message.method)})`,
      raw: rawResponse,
    };
  }

  const result = message.result;
  if (!result || typeof result !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response missing message.result",
      raw: rawResponse,
    };
  }

  // Floor refusal
  if (result.error) {
    return {
      ok: false,
      kind: "floor",
      error: result.error,
      floor: result.floor ?? null,
      requested: result.requested ?? null,
      targetCollection: result.targetCollection ?? null,
      lambdaVersion: _debugVersion ?? null,
    };
  }

  // Success — both baselineState and events arrays must be present
  if (!Array.isArray(result.baselineState)) {
    return {
      ok: false,
      kind: "error",
      reason: "Response result.baselineState is missing or not an array",
      raw: rawResponse,
    };
  }
  if (!Array.isArray(result.events)) {
    return {
      ok: false,
      kind: "error",
      reason: "Response result.events is missing or not an array",
      raw: rawResponse,
    };
  }

  return {
    ok: true,
    kind: "success",
    baselineState: result.baselineState,
    events: result.events,
    baselineSnapshot: result.baselineSnapshot ?? null,
    seedToStartReplayWindow: result.seedToStartReplayWindow ?? null,
    dayWindow: result.dayWindow ?? null,
    meta: result.meta ?? null,
    lambdaVersion: _debugVersion ?? null,
  };
}
